import React, { useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Fragment } from 'react';
import {
    UpdateWindow,
    CreateUpdateWindowData,
    CreateUpdateWindowSchema,
    EditUpdateWindowData
} from '../../models/updateWindow';

interface UpdateWindowModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: CreateUpdateWindowData | EditUpdateWindowData) => Promise<void>;
    editingWindow?: UpdateWindow | null;
    isLoading?: boolean;
}

const UpdateWindowModal: React.FC<UpdateWindowModalProps> = ({
    isOpen,
    onClose,
    onSave,
    editingWindow,
    isLoading = false
}) => {
    const isEditing = !!editingWindow;

    // Helper para extrair mensagem de erro
    const getErrorMessage = (error: any) => {
        if (typeof error === 'string') return error;
        if (error && typeof error === 'object' && 'message' in error) return error.message;
        return '';
    };

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<CreateUpdateWindowData>({
        resolver: zodResolver(CreateUpdateWindowSchema),
        defaultValues: {
            title: '',
            description: '',
            startDate: '',
            endDate: '',
            isActive: true,
            allowMultipleUpdates: true
        }
    });

    // Reset form when modal opens/closes or editing window changes
    useEffect(() => {
        if (isOpen) {
            if (editingWindow) {
                // Converter datas para formato datetime-local
                const formatDateForInput = (dateString: string) => {
                    const date = new Date(dateString);
                    return date.toISOString().slice(0, 16);
                };

                reset({
                    title: editingWindow.title,
                    description: editingWindow.description || '',
                    startDate: formatDateForInput(editingWindow.startDate),
                    endDate: formatDateForInput(editingWindow.endDate),
                    isActive: editingWindow.isActive,
                    isRequired: editingWindow.isRequired || false,
                    allowMultipleUpdates: editingWindow.allowMultipleUpdates
                });
            } else {
                reset({
                    title: '',
                    description: '',
                    startDate: '',
                    endDate: '',
                    isActive: true,
                    isRequired: false,
                    allowMultipleUpdates: true
                });
            }
        }
    }, [isOpen, editingWindow, reset]);

    const onSubmit = async (data: CreateUpdateWindowData) => {
        try {
            // Converter para ISO string
            const formattedData = {
                ...data,
                startDate: new Date(data.startDate).toISOString(),
                endDate: new Date(data.endDate).toISOString()
            };

            await onSave(formattedData);
            onClose();
        } catch (error) {
            // Erro já tratado pelo componente pai
            console.error('Erro ao salvar janela:', error);
        }
    };

    const handleClose = () => {
        if (isSubmitting || isLoading) return;
        onClose();
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        {isEditing ? 'Editar Janela de Atualização' : 'Nova Janela de Atualização'}
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        disabled={isSubmitting || isLoading}
                                        className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                    {/* Título */}
                                    <div>
                                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                                            Título *
                                        </label>
                                        <input
                                            {...register('title')}
                                            type="text"
                                            id="title"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            placeholder="Ex: Atualização Cadastral Q1 2024"
                                        />
                                        {errors.title && (
                                            <p className="mt-1 text-sm text-red-600">{getErrorMessage(errors.title)}</p>
                                        )}
                                    </div>

                                    {/* Descrição */}
                                    <div>
                                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                            Descrição
                                        </label>
                                        <textarea
                                            {...register('description')}
                                            id="description"
                                            rows={3}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            placeholder="Descrição opcional da janela de atualização"
                                        />
                                        {errors.description && (
                                            <p className="mt-1 text-sm text-red-600">{getErrorMessage(errors.description)}</p>
                                        )}
                                    </div>

                                    {/* Data e hora de início */}
                                    <div>
                                        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                                            Data e Hora de Início *
                                        </label>
                                        <input
                                            {...register('startDate')}
                                            type="datetime-local"
                                            id="startDate"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                        {errors.startDate && (
                                            <p className="mt-1 text-sm text-red-600">{getErrorMessage(errors.startDate)}</p>
                                        )}
                                    </div>

                                    {/* Data e hora de fim */}
                                    <div>
                                        <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                                            Data e Hora de Fim *
                                        </label>
                                        <input
                                            {...register('endDate')}
                                            type="datetime-local"
                                            id="endDate"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        />
                                        {errors.endDate && (
                                            <p className="mt-1 text-sm text-red-600">{getErrorMessage(errors.endDate)}</p>
                                        )}
                                    </div>

                                    {/* Checkboxes */}
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <input
                                                {...register('isActive')}
                                                type="checkbox"
                                                id="isActive"
                                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                                                Janela ativa
                                            </label>
                                        </div>

                                        <div className="flex items-center">
                                            <input
                                                {...register('isRequired')}
                                                type="checkbox"
                                                id="isRequired"
                                                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="isRequired" className="ml-2 block text-sm text-gray-700">
                                                <span className="font-medium text-red-700">Janela obrigatória</span>
                                                <span className="block text-xs text-gray-500">Força colaboradores a atualizar</span>
                                            </label>
                                        </div>

                                        <div className="flex items-center">
                                            <input
                                                {...register('allowMultipleUpdates')}
                                                type="checkbox"
                                                id="allowMultipleUpdates"
                                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="allowMultipleUpdates" className="ml-2 block text-sm text-gray-700">
                                                Permitir múltiplas atualizações
                                            </label>
                                        </div>
                                    </div>

                                    {/* Botões */}
                                    <div className="flex justify-end space-x-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            disabled={isSubmitting || isLoading}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting || isLoading}
                                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                        >
                                            {isSubmitting || isLoading ? 'Salvando...' : (isEditing ? 'Salvar' : 'Criar')}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default UpdateWindowModal;